import { Link } from "react-router-dom";
import { Linkedin, Youtube, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { NewsletterForm } from "./SubmissionForm";

const Footer = () => {
  const { t } = useLanguage();
  const socials = [
    { icon: Linkedin, label: "LinkedIn" },
    { icon: Youtube, label: "YouTube" },
    { icon: Facebook, label: "Facebook" },
    { icon: Instagram, label: "Instagram" },
  ];

  const cols = [
    {
      title: t("footer.explore"),
      links: [
        ["/association", "navX.presentation"],
        ["/mission", "nav.mission"],
        ["/histoire", "nav.histoire"],
        ["/evenements", "navX.evenements"],
        ["/transparence", "nav.transparence"],
        ["/actualites", "nav.actualites"],
      ],
    },
    {
      title: t("footer.researchCol"),
      links: [
        ["/recherche", "nav.recherche"],
        ["/centre", "nav.centre"],
        ["/programme", "nav.programme"],
        ["/projets", "navX.projets"],
        ["/feuille-de-route", "nav.roadmap"],
      ],
    },
    {
      title: t("nav.join"),
      links: [
        ["/scientifiques", "nav.scientifiques"],
        ["/partenariats", "nav.partenariats"],
        ["/soutenir", "nav.soutenir"],
        ["/contact", "nav.contact"],
      ],
    },
  ];

  return (
    <footer className="bg-graphite text-white structural-grid-light" data-testid="main-footer">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-10 border-b border-white/10 py-16 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="overline-mono text-white/50">Green Horizon Project</p>
            <h2 className="mt-4 font-display text-3xl font-light tracking-tight md:text-4xl">
              {t("footer.newsletterTitle")}
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-white/60">{t("footer.newsletterText")}</p>
          </div>
          <NewsletterForm context="footer" dark />
        </div>

        <div className="grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3">
              <img src="/assets/logo.png" alt="Logo Green Horizon Project" className="h-11 w-11 bg-white object-contain p-0.5" data-testid="footer-logo" />
              <span className="font-display text-sm font-extrabold uppercase tracking-tight">
                Green Horizon <span className="font-light tracking-[0.3em] text-white/60">Project</span>
              </span>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">{t("footer.baseline")}</p>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">{t("brand.tagline")}</p>
            <div className="mt-6">
              <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
                {t("footer.socialTitle")}
              </p>
              <div className="flex gap-3">
                {socials.map(({ icon: Icon, label }) => (
                  <button
                    key={label}
                    onClick={() => toast(t("footer.socialSoon"))}
                    aria-label={label}
                    data-testid={`social-${label.toLowerCase()}-button`}
                    className="flex h-10 w-10 items-center justify-center border border-white/15 text-white/60 transition-colors duration-200 hover:border-white hover:text-white"
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          {cols.map((col) => (
            <div key={col.title}>
              <p className="overline-mono text-white/40">{col.title}</p>
              <ul className="mt-5 space-y-3">
                {col.links.map(([to, key]) => (
                  <li key={to}>
                    <Link
                      to={to}
                      className="text-sm text-white/70 transition-colors duration-200 hover:text-white"
                      data-testid={`footer-link-${key.split(".")[1]}`}
                    >
                      {t(key)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-4 border-t border-white/10 py-8 md:flex-row md:items-center md:justify-between">
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/40">
            © {new Date().getFullYear()} Green Horizon Project — {t("footer.rights")}
          </p>
          <div className="flex flex-wrap gap-6">
            <Link to="/mentions-legales" className="text-xs text-white/50 transition-colors hover:text-white" data-testid="footer-link-mentions">
              {t("footer.mentions")}
            </Link>
            <Link to="/confidentialite" className="text-xs text-white/50 transition-colors hover:text-white" data-testid="footer-link-privacy">
              {t("footer.privacy")}
            </Link>
            <Link to="/admin/login" className="text-xs text-white/50 transition-colors hover:text-white" data-testid="footer-link-admin">
              {t("admin.loginTitle")}
            </Link>
          </div>
        </div>
        <p className="border-t border-white/10 py-6 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30">
          {t("footer.legalLine")}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
